/**
 * Autocomplete dropdown for @mentions in chat.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Typography,
  Avatar,
  CircularProgress,
} from '@mui/joy';
import { User as PersonIcon } from '@phosphor-icons/react';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

interface MentionAutocompleteProps {
  searchQuery: string;
  onSelect: (user: User) => void;
  onClose: () => void;
  position: { top: number; left: number };
  currentUserId?: string | number;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  searchQuery,
  onSelect,
  onClose,
  position,
  currentUserId,
}) => {
  const { users, loading } = useOrganizationUsers();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter users based on search query (exclude current user)
  const filteredUsers = users.filter((user) => {
    if (currentUserId && String(user.id) === String(currentUserId)) {
      return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  }).slice(0, 8); // Limit to 8 results

  // Reset selection when filtered users change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredUsers[selectedIndex]) {
          onSelect(filteredUsers[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filteredUsers, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (list) {
      const selectedItem = list.children[selectedIndex] as HTMLElement;
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: 1400,
          bgcolor: 'background.surface',
          borderRadius: 'md',
          boxShadow: 'lg',
          p: 2,
          minWidth: 200,
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (filteredUsers.length === 0) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: 1400,
          bgcolor: 'background.surface',
          borderRadius: 'md',
          boxShadow: 'lg',
          p: 2,
          minWidth: 200,
        }}
      >
        <Typography level="body-sm" color="neutral">
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1400,
        bgcolor: 'background.surface',
        borderRadius: 'md',
        boxShadow: 'lg',
        maxHeight: 300,
        overflow: 'auto',
        minWidth: 250,
      }}
    >
      <List ref={listRef} size="sm" sx={{ '--ListItem-paddingY': '8px' }}>
        {filteredUsers.map((user, index) => (
          <ListItem key={user.id}>
            <ListItemButton
              selected={index === selectedIndex}
              onClick={() => onSelect(user)}
              sx={{
                borderRadius: 'sm',
              }}
            >
              <ListItemDecorator>
                <Avatar size="sm">
                  {user.first_name?.[0] || user.email?.[0] || <PersonIcon />}
                </Avatar>
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="body-sm" fontWeight="md">
                  {user.full_name || `${user.first_name} ${user.last_name}`.trim()}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {user.email}
                </Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default MentionAutocomplete;
